import type { ReactElement, FC, ReactNode } from 'react';
import { omitBy, isUndefined } from 'lodash';
import { createContext, useCallback, useContext } from 'react';
import { useSessionStorage as useHooksTsSessionStorage } from 'usehooks-ts';
import { z } from 'zod';
import { appointmentProviderSchema } from '@provider-directory/types/providers.contracts';

export const APPOINTMENT_STORAGE_KEY = 'appointment';

export const SlotDateMode = z.enum(['nearest', 'selected']);
export type SlotDateMode = z.infer<typeof SlotDateMode>;

export const appointmentType = z
  .object({
    currentScene: z.number(),
    provider: appointmentProviderSchema,
    visitTypeId: z.string().nullable(),
    departmentId: z.string().nullable(),
    slotDate: z.string().nullable(),
    slotDateMode: SlotDateMode.nullable(),
    slotTime: z.string().nullable(),
    showCalendarByDefault: z.boolean(),
    firstName: z.string().nullable(),
    middleName: z.string().nullable(),
    lastName: z.string().nullable(),
    dob: z.string().nullable(),
    legalSex: z.string().nullable(),
    socialSecurityNumber: z.string().nullable(),
    email: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    phoneType: z.enum(['home', 'work', 'mobile', 'other']).nullable(),
    smsOptIn: z.boolean(),
    streetAddress: z.string().nullable(),
    apartment: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    zip: z.string().nullable(),
    comments: z.string().nullable(),
    usingInsurance: z.string().nullable(), // boolean? returns "yes" / "no" currently
    insuranceCarrier: z.string().nullable(),
    memberID: z.string().nullable(),
    groupNumber: z.string().nullable(),
    duration: z.number().nullable(),
    patientInstructions: z.array(z.string()),
    specialtyBranch: z.string().nullable(),
    reasonForVisit: z.string().nullable(), // Internal RFV name with derivations like 50-and-over or medicaid
    reasonForVisitKey: z.string().nullable(), // External RFV name without derivations
  })
  .strict();
export type AppointmentType = z.infer<typeof appointmentType>;

export const partialAppointmentType = appointmentType.partial();
export type PartialAppointmentType = z.infer<typeof partialAppointmentType>;

export const DEFAULT_APPOINTMENT: AppointmentType = {
  currentScene: 0,
  provider: null,
  visitTypeId: null,
  reasonForVisit: null,
  reasonForVisitKey: null,
  departmentId: null,
  slotDate: null,
  slotDateMode: null,
  slotTime: null,
  showCalendarByDefault: false,
  firstName: null,
  middleName: null,
  lastName: null,
  dob: null,
  legalSex: null,
  socialSecurityNumber: null,
  email: null,
  phoneNumber: null,
  phoneType: null,
  smsOptIn: false,
  streetAddress: null,
  apartment: null,
  city: null,
  state: null,
  zip: null,
  comments: null,
  usingInsurance: null,
  insuranceCarrier: null,
  memberID: null,
  groupNumber: null,
  duration: null,
  patientInstructions: [],
  specialtyBranch: null,
};

export interface AppointmentContextType {
  appointment: AppointmentType;
  updateAppointment: (patch: PartialAppointmentType) => void;
  resetAppointment: (patch?: PartialAppointmentType) => void;
}

const AppointmentContext = createContext<AppointmentContextType | null>(null);

const AppointmentProvider: FC<{ children: ReactNode }> = ({
  children,
}): ReactElement => {
  const [appointment, setAppointment] =
    useHooksTsSessionStorage<AppointmentType>(
      APPOINTMENT_STORAGE_KEY,
      DEFAULT_APPOINTMENT,
    );

  const updateAppointment = useCallback(
    (patch: PartialAppointmentType) => {
      const appointmentPatch: PartialAppointmentType =
        partialAppointmentType.parse(patch);
      const newAppointment: AppointmentType = {
        ...appointment,
        ...omitBy(appointmentPatch, isUndefined),
      };
      setAppointment(newAppointment);
    },
    [appointment, setAppointment],
  );

  const resetAppointment = (newAppointmentData?: PartialAppointmentType) => {
    const newAppointment = {
      ...DEFAULT_APPOINTMENT,
      ...omitBy(newAppointmentData, isUndefined),
    };
    setAppointment(newAppointment);
  };

  return (
    <AppointmentContext.Provider
      value={{ appointment, updateAppointment, resetAppointment }}
    >
      {children}
    </AppointmentContext.Provider>
  );
};

export const useAppointment = (): AppointmentContextType => {
  const appointmentContext = useContext(AppointmentContext);

  if (!appointmentContext) {
    throw new Error(
      'useAppointment must be used within <AppointmentContext.Provider>',
    );
  }
  return appointmentContext;
};

export { AppointmentProvider, AppointmentContext };
